/**
 * narration-player.ts — Real-time lip sync via HeadAudio + ElevenLabs TTS.
 *
 * Flow:
 *   1. On first call, sets up HeadAudio (AudioWorklet + pre-trained model).
 *   2. speak(text) → POST /narration/speak → base64 MP3 from ElevenLabs.
 *   3. Decoded AudioBuffer plays through two branches:
 *      a. → HeadAudio worklet: detects Oculus visemes in real-time from
 *           the audio signal (no timestamps needed).
 *      b. → AudioContext.destination: the user hears the speech.
 *   4. HeadAudio.onvalue fires (key="viseme_aa", value=0..1) every frame.
 *      We accumulate those alphas in `currentVisemeAlphas` and, on each
 *      render-loop `update()`, convert them to canonical ARKit weights via
 *      `viseme-map.ts` and call `figure.setVisemeWeights()`.
 *      The figure's morph mixer max-merges the viseme layer with ambient/
 *      beat on lip shapes only — so emotion expressions and lip sync coexist
 *      cleanly, and rigs with different morph naming (CC `V_Open`, Avaturn
 *      `jawOpen`, Asuna `JawOpen`) all receive the same canonical write.
 *   5. Call narrationPlayer.update(dtMs) from your render loop.
 */

// HeadAudio ships a plain JS module. Import the class directly.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — no bundled types in @met4citizen/headaudio
import { HeadAudio } from '@met4citizen/headaudio';

import { visemeAlphasToArkitWeights } from '../motion/viseme-map';

/** Minimal Figure subset we need — avoids a deep import of figure.ts. */
interface FigureLike {
  morphTargetNames: string[];
  setVisemeWeights: (weights: Record<string, number>) => void;
}

/** Master volume for all narration playback. Range 0–1. */
const NARRATION_VOLUME = 0.30;

export class NarrationPlayer {
  private readonly serverUrl: string;
  private audioCtx: AudioContext | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private headAudio: any = null;
  /**
   * Live viseme alpha buffer. HeadAudio fires `onvalue(key, value)` per
   * viseme per worklet tick; we keep the latest value for each key here
   * and flush to the figure once per render frame in `update(dtMs)`.
   * Pushing to the figure on every fire would call `setVisemeWeights`
   * dozens of times per frame for no extra fidelity.
   */
  private currentVisemeAlphas: Record<string, number> = {};
  private figure: FigureLike | null = null;
  private ready = false;
  private preloadedBuffer: AudioBuffer | null = null;

  constructor(serverUrl: string) {
    this.serverUrl = serverUrl;
  }

  // ── Setup ────────────────────────────────────────────────────────────────

  /**
   * Initialise the AudioContext, register the worklet processor, load the
   * pre-trained viseme model, and build the morph-target lookup for the
   * given figure. Safe to call multiple times — subsequent calls update the
   * morph map if the character changes.
   */
  async setup(figure: FigureLike): Promise<void> {
    console.log('[narration] setup() called, audioCtx exists:', !!this.audioCtx);
    if (!this.audioCtx) {
      this.audioCtx = new AudioContext();
      console.log('[narration] AudioContext created, state:', this.audioCtx.state);
      await this.audioCtx.audioWorklet.addModule('/headworklet.min.mjs');
      console.log('[narration] worklet module registered');

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.headAudio = new HeadAudio(this.audioCtx, {
        parameterData: {
          vadGateActiveDb: -40,
          vadGateInactiveDb: -60,
        },
      });
      console.log('[narration] HeadAudio node created');

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      await this.headAudio.loadModel('/model-en-mixed.bin');
      console.log('[narration] HeadAudio worklet + model loaded');
    }

    // Bind the figure for `update()` to flush viseme weights into. No more
    // per-mesh morph index — the figure's mixer handles per-rig morph
    // routing centrally (canonical ARKit aliases + CC fan-out for V_*).
    this.figure = figure;
    console.log(
      `[narration] figure bound — ${figure.morphTargetNames.length} morph targets visible to mixer`,
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    this.headAudio.onvalue = (key: string, value: number) => {
      // Buffer the latest alpha per viseme key. Flushed to figure in update().
      this.currentVisemeAlphas[key] = value;
    };

    this.ready = true;
  }

  // ── Preload ───────────────────────────────────────────────────────────────

  /**
   * Fetch and decode TTS audio without playing it yet. Call this while a
   * loading overlay is still showing so the audio is ready the instant you
   * call playPreloaded(). Resolves when the AudioBuffer is decoded and ready.
   */
  async preload(text: string, voiceId?: string): Promise<void> {
    if (!this.ready || !this.audioCtx) {
      console.warn('[narration] preload called but not ready');
      return;
    }
    if (this.audioCtx.state === 'suspended') await this.audioCtx.resume();
    try {
      console.log(`[narration] preloading TTS: "${text.slice(0, 80)}"${voiceId ? ` (voice: ${voiceId})` : ''}`);
      const res = await fetch(`${this.serverUrl}/narration/speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, ...(voiceId ? { voiceId } : {}) }),
      });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text().catch(() => res.statusText)}`);
      const { audioBase64 } = (await res.json()) as { audioBase64: string };
      const bytes = Uint8Array.from(atob(audioBase64), (c) => c.charCodeAt(0));
      this.preloadedBuffer = await this.audioCtx.decodeAudioData(bytes.buffer);
      console.log('[narration] preload complete — audio ready');
    } catch (err) {
      console.warn('[narration] preload failed:', err);
    }
  }

  /**
   * Play the buffer that was previously preloaded. No-op if preload hasn't
   * completed or failed. Clears the stored buffer after starting playback.
   */
  playPreloaded(): void {
    if (!this.preloadedBuffer || !this.audioCtx || !this.headAudio) return;
    const gain = this.audioCtx.createGain();
    gain.gain.value = NARRATION_VOLUME;
    const source = this.audioCtx.createBufferSource();
    source.buffer = this.preloadedBuffer;
    source.connect(gain);
    gain.connect(this.audioCtx.destination);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    source.connect(this.headAudio);
    source.start();
    this.preloadedBuffer = null;
    console.log('[narration] playing preloaded audio');
  }

  // ── Speak ────────────────────────────────────────────────────────────────

  /**
   * Convert `text` to speech via ElevenLabs (server-side) and play it back,
   * driving the character's lip blend shapes in real-time through HeadAudio.
   */
  async speak(text: string, voiceId?: string): Promise<void> {
    if (!this.ready || !this.audioCtx || !this.headAudio) {
      console.warn('[narration] speak called but not ready. ready=%s audioCtx=%s headAudio=%s',
        this.ready, !!this.audioCtx, !!this.headAudio);
      return;
    }

    // Resume if browser suspended the context (autoplay policy).
    if (this.audioCtx.state === 'suspended') {
      console.log('[narration] resuming suspended AudioContext');
      await this.audioCtx.resume();
    }
    console.log('[narration] AudioContext state:', this.audioCtx.state);

    try {
      console.log(`[narration] fetching TTS for: "${text.slice(0, 80)}"${voiceId ? ` (voice: ${voiceId})` : ''}`);
      const res = await fetch(`${this.serverUrl}/narration/speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, ...(voiceId ? { voiceId } : {}) }),
      });
      if (!res.ok) {
        const msg = await res.text().catch(() => res.statusText);
        throw new Error(`${res.status}: ${msg.slice(0, 200)}`);
      }
      const { audioBase64 } = (await res.json()) as { audioBase64: string };

      // Decode base64 → ArrayBuffer → AudioBuffer.
      const bytes = Uint8Array.from(atob(audioBase64), (c) => c.charCodeAt(0));
      const audioBuffer = await this.audioCtx.decodeAudioData(bytes.buffer);

      // Branch 1: playback for the user (through gain for volume control).
      const gain = this.audioCtx.createGain();
      gain.gain.value = NARRATION_VOLUME;
      const source = this.audioCtx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(gain);
      gain.connect(this.audioCtx.destination);

      // Branch 2: HeadAudio analysis → onvalue callbacks → morph targets.
      // HeadAudio is a sink (no audio output) so we connect separately.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      source.connect(this.headAudio);

      source.start();
      console.log('[narration] playing');
    } catch (err) {
      console.warn('[narration] speak failed:', err);
    }
  }

  // ── Speak from pre-generated URL ─────────────────────────────────────────

  /**
   * Fetch a pre-generated audio file by URL and play it immediately through
   * HeadAudio for real-time mouth sync. Used for per-beat vocalizations that
   * were pre-rendered server-side (no round-trip to /narration/speak needed).
   *
   * @param url       Absolute URL of the MP3 file to play.
   * @param offsetSec Seconds to wait before starting playback. Default 0.
   */
  async speakFromUrl(url: string, offsetSec = 0): Promise<void> {
    if (!this.ready || !this.audioCtx || !this.headAudio) {
      console.warn('[narration] speakFromUrl called but not ready');
      return;
    }
    if (this.audioCtx.state === 'suspended') await this.audioCtx.resume();
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`${res.status} fetching vocalization`);
      const arrayBuffer = await res.arrayBuffer();
      const audioBuffer = await this.audioCtx.decodeAudioData(arrayBuffer);

      const gain = this.audioCtx.createGain();
      gain.gain.value = NARRATION_VOLUME;
      const source = this.audioCtx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(gain);
      gain.connect(this.audioCtx.destination);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      source.connect(this.headAudio);
      source.start(this.audioCtx.currentTime + offsetSec);
      console.log(`[narration] vocalization playing (offset=${offsetSec}s): ${url.split('/').pop()}`);
    } catch (err) {
      console.warn('[narration] speakFromUrl failed:', err);
    }
  }

  // ── Per-frame update ──────────────────────────────────────────────────────

  /**
   * Call this every frame from your render loop, passing delta time in ms.
   * Advances the HeadAudio worklet (so it can decay viseme alphas naturally)
   * and flushes the buffered alphas to the figure's lip-sync layer once per
   * frame as canonical ARKit weights.
   */
  update(dtMs: number): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.headAudio?.update(dtMs);

    if (!this.figure) return;
    // Convert Oculus visemes → canonical ARKit and hand off to the figure.
    // The figure decides per-rig fan-out (CC V_* + Jaw_Open, or pass-through
    // for Avaturn / Asuna). Sending the empty dict during silence is fine —
    // it lerps the lip layer back to zero.
    const arkit = visemeAlphasToArkitWeights(this.currentVisemeAlphas);
    this.figure.setVisemeWeights(arkit);
  }
}
